/**
 * Phase 36a — Axis Bank CIT portal (Wave 1).
 * Van fleet list for BANK_P2 tenants.
 */
import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Truck, Plus, Search, Loader2, Edit2, RefreshCw, Battery, Wifi, WifiOff,
} from 'lucide-react';

const BLANK = {
  vehicle_number: '', cit_vendor_id: '', home_chest_location_id: '', device_id: '',
  make: '', model: '', year: '', capacity_kg: '',
};

export default function OpsVansPage() {
  const { tenant }                = useTrackOticAuth();
  const [loading, setLoading]     = useState(true);
  const [saving,  setSaving]      = useState(false);
  const [vans,    setVans]        = useState([]);
  const [vendors, setVendors]     = useState([]);
  const [chests,  setChests]      = useState([]);
  const [devices, setDevices]     = useState([]);
  const [searchQuery, setSearchQ] = useState('');
  const [vendorFilter, setVendor] = useState('');
  const [statusFilter, setStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(BLANK);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (vendorFilter) params.vendor_id = vendorFilter;
      if (statusFilter) params.status    = statusFilter;
      const [vansRes, vendorsRes] = await Promise.all([
        opsApi.getVans(params),
        opsApi.getCitVendors({}),
      ]);
      setVans(vansRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load vans');
      setVans([]);
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load chests and devices for the picker (shared by create + edit)
  const loadPickers = async () => {
    try {
      const [locRes, devRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/locations?type=currency_chest&limit=200`,
              { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/devices?limit=200`,
              { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
      ]);
      const locs = Array.isArray(locRes) ? locRes : (locRes.data || []);
      const devs = Array.isArray(devRes) ? devRes : (devRes.data || []);
      setChests(locs);
      // /api/v1/devices returns {device_id, trk_id, ...} — normalize for the picker
      setDevices(devs.map((d) => ({
        id:         d.id ?? d.device_id,
        device_uid: d.device_uid ?? d.trk_id,
      })));
    } catch { /* non-fatal */ }
  };

  const openCreate = async () => {
    setEditing(null);
    setForm(BLANK);
    await loadPickers();
    setDialogOpen(true);
  };

  const openEdit = async (v) => {
    setEditing(v);
    setForm({
      vehicle_number:         v.vehicle_number || '',
      cit_vendor_id:          v.cit_vendor_id || '',
      home_chest_location_id: v.home_chest_location_id || '',
      device_id:              v.device_id || '',
      make:  v.make || '', model: v.model || '',
      year:  v.year || '', capacity_kg: v.capacity_kg || '',
    });
    await loadPickers();
    setDialogOpen(true);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [vendorFilter, statusFilter]);

  const handleSave = async () => {
    if (!form.vehicle_number.trim()) { toast.error('Registration (van ID) is required'); return; }
    const payload = {
      vehicle_number:         form.vehicle_number,
      cit_vendor_id:          form.cit_vendor_id || null,
      home_chest_location_id: form.home_chest_location_id || null,
      device_id:              form.device_id || null,
      make:                   form.make || null,
      model:                  form.model || null,
      year:                   form.year ? Number(form.year) : null,
      capacity_kg:            form.capacity_kg ? Number(form.capacity_kg) : null,
    };
    setSaving(true);
    try {
      if (editing) {
        await opsApi.updateVan(editing.id, payload);
        toast.success('Van updated');
      } else {
        await opsApi.createVan(payload);
        toast.success('Van added');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const filtered = vans.filter(
    (v) => v.vehicle_number?.toLowerCase().includes(searchQuery.toLowerCase())
        || v.cit_vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const onDutyCount  = vans.filter((v) => v.status === 'on_duty').length;
  const offlineCount = vans.filter((v) => v.status === 'offline').length;

  return (
    <div className="p-8" data-testid="ops-vans-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Truck className="w-8 h-8 text-teal-500" />
            Vans
          </h1>
          <p className="text-slate-400 mt-1">CIT fleet for {tenant?.name}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchAll} variant="outline" className="border-slate-700 text-slate-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="ops-add-van-btn">
            <Plus className="w-4 h-4 mr-2" /> Add van
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Truck} label="Total" value={vans.length} />
        <StatCard icon={Wifi} label="On duty" value={onDutyCount} valueClass="text-green-400" />
        <StatCard icon={WifiOff} label="Offline" value={offlineCount} valueClass="text-amber-400" />
        <StatCard icon={Battery} label="KM today (fleet)" value={vans.reduce((s, v) => s + (v.km_today || 0), 0).toFixed(1)} />
      </div>

      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
          <Input
            placeholder="Search by van ID or vendor..."
            value={searchQuery}
            onChange={(e) => setSearchQ(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-white"
          />
        </div>
        <select
          value={vendorFilter}
          onChange={(e) => setVendor(e.target.value)}
          className="rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
        >
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="maintenance">Maintenance</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Van ID</TableHead>
              <TableHead className="text-slate-400">Vendor</TableHead>
              <TableHead className="text-slate-400">Device</TableHead>
              <TableHead className="text-slate-400">Battery</TableHead>
              <TableHead className="text-slate-400">Driver now</TableHead>
              <TableHead className="text-slate-400">Home chest</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">KM today</TableHead>
              <TableHead className="text-slate-400">KM month</TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
              </TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-500">
                No vans found
              </TableCell></TableRow>
            ) : (
              filtered.map((v) => (
                <TableRow key={v.id} className="border-slate-800" data-testid={`ops-van-row-${v.id}`}>
                  <TableCell className="text-white font-medium">{v.vehicle_number}</TableCell>
                  <TableCell className="text-slate-300">{v.cit_vendor_name || '—'}</TableCell>
                  <TableCell className="text-slate-300">{v.device_uid || '—'}</TableCell>
                  <TableCell>
                    {v.battery_level !== null && v.battery_level !== undefined ? (
                      <span className={`font-mono ${v.battery_level < 30 ? 'text-red-400' : v.battery_level < 50 ? 'text-amber-400' : 'text-green-400'}`}>
                        {v.battery_level}%
                      </span>
                    ) : <span className="text-slate-500">—</span>}
                  </TableCell>
                  <TableCell className="text-slate-300">{v.driver_now_name || '—'}</TableCell>
                  <TableCell className="text-slate-300">{v.home_chest_name || '—'}</TableCell>
                  <TableCell><Badge className={vanStatusBadge(v.status)}>{vanStatusLabel(v.status)}</Badge></TableCell>
                  <TableCell className="text-slate-200 font-mono">{(v.km_today ?? 0).toFixed(1)}</TableCell>
                  <TableCell className="text-slate-200 font-mono">{(v.km_this_month ?? 0).toFixed(1)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(v)}
                            className="h-8 w-8 text-slate-400 hover:text-teal-400" data-testid={`edit-van-${v.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? 'Edit van' : 'Add van'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Register a van. New vans require admin approval before dispatching.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <FormField label="Registration (van ID)" value={form.vehicle_number} onChange={(v) => setForm({ ...form, vehicle_number: v })} span2 placeholder="MH01-AX-1001" />
            <div className="col-span-2">
              <Label className="text-slate-300">CIT vendor</Label>
              <select
                value={form.cit_vendor_id}
                onChange={(e) => setForm({ ...form, cit_vendor_id: e.target.value })}
                className="w-full mt-1 rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
              >
                <option value="">— None —</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-slate-300">Home chest</Label>
              <select
                value={form.home_chest_location_id}
                onChange={(e) => setForm({ ...form, home_chest_location_id: e.target.value })}
                className="w-full mt-1 rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
              >
                <option value="">— None —</option>
                {chests.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-slate-300">Device</Label>
              <select
                value={form.device_id}
                onChange={(e) => setForm({ ...form, device_id: e.target.value })}
                className="w-full mt-1 rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
              >
                <option value="">— None —</option>
                {devices.map((d) => <option key={d.id} value={d.id}>{d.device_uid}</option>)}
              </select>
            </div>
            <FormField label="Make"  value={form.make}  onChange={(v) => setForm({ ...form, make: v })} />
            <FormField label="Model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} />
            <FormField label="Year"  type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} />
            <FormField label="Capacity (kg)" type="number" value={form.capacity_kg} onChange={(v) => setForm({ ...form, capacity_kg: v })} />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSave} disabled={saving} data-testid="save-van-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save changes' : 'Add van')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function vanStatusBadge(s) {
  switch (s) {
    case 'on_duty':   return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'idle':      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'offline':   return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'no_device': return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    default:          return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}
function vanStatusLabel(s) {
  switch (s) {
    case 'on_duty':   return 'On duty';
    case 'idle':      return 'Idle';
    case 'offline':   return 'Device offline';
    case 'no_device': return 'No device';
    default:          return s || '—';
  }
}

function StatCard({ icon: Icon, label, value, valueClass = 'text-white' }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
        <Icon className="w-5 h-5 text-teal-400" />
      </div>
      <div>
        <p className="text-slate-500 text-xs uppercase">{label}</p>
        <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', span2 = false, placeholder = '' }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <Label className="text-slate-300">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 bg-slate-900 border-slate-800 text-white"
      />
    </div>
  );
}
